'use client';

import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import SafeImage from '@ui/SafeImage';
import {
  ChevronRightIcon,
  TrendingUpIcon,
  CheckIcon,
  CheckCircleIcon,
} from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { FirebaseCallable, functions } from '@util/firebase';
import { ContactUsForm } from 'app/contact/components/ContactUs';
import { useToastContext } from 'context/ToastContext';
import { logError } from '@util/logError';
import { prefix } from '@util/imagePrefix';

const formSchema = z.object({
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone number is required'),
  currentSelling: z.string().min(1, 'This field is required'),
});

export type IntegrationsFormData = z.infer<typeof formSchema>;

export default function IntegrationsPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { showSuccessToast } = useToastContext();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IntegrationsFormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: IntegrationsFormData) => {
    try {
      setLoading(true);

      const contactData = {
        name: '',
        message: data.currentSelling,
        email: data.email,
        phone: data.phone,
        topic: 'integrations',
      };

      await httpsCallable<ContactUsForm, { data: 'success' }>(
        functions,
        FirebaseCallable.contactUs
      )(contactData);

      showSuccessToast(
        "Thank you for your interest! We'll get back to you soon to discuss integration options."
      );

      setSubmitted(true);
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      logError(error, 'Error submitting the form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex h-full w-full flex-col items-center justify-center bg-brand-secondary px-8 py-32 sm:h-screen sm:flex-row sm:px-0">
        <div className="flex h-full w-full flex-col items-center justify-between sm:mx-auto sm:max-w-default sm:flex-row">
          <div className="flex flex-col items-center justify-center gap-y-8 px-8 sm:px-0">
            <div className="flex w-fit items-center justify-center gap-x-4 rounded-full bg-black p-4 px-8 text-[1.6rem] text-white sm:w-[45rem] sm:px-4 sm:text-[1.8rem]">
              <TrendingUpIcon className="text-brand-secondary" />

              <span className="font-semibold">
                Join over 50,000+ sellers on MX Locker!
              </span>
            </div>

            <h1 className="w-full text-center text-[3.8rem] font-bold text-white sm:w-[85rem] sm:text-[4.7rem]">
              Integrations to power your business on MX Locker.
            </h1>

            <h1 className="text-center text-[1.8rem] font-medium text-white sm:text-[2.5rem]">
              Sell more online, and spend less time doing it.
            </h1>

            <SafeImage
              src={`${prefix}/assets/integrations/integrations-orbit-banner_1920x2560.png`}
              alt="MX Locker integrations"
              className="h-auto w-full sm:w-[73.5rem]"
              height={1091}
              width={1764}
            />
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-32 flex h-full w-full flex-col gap-y-16 rounded-[3rem] bg-white p-16 sm:mt-0 sm:max-h-[80rem] sm:w-[61rem]"
          >
            <FormLabel value="Email" required className="!text-[2.1rem]">
              <FormInput
                placeholder="Enter your email"
                className="sm:!text-[2.1rem]"
                type="email"
                error={!!errors.email}
                {...register('email')}
                autoComplete="email"
              />
              {errors.email && (
                <span className="mt-2 text-[1.6rem] text-red-500">
                  {errors.email.message}
                </span>
              )}
            </FormLabel>

            <FormLabel value="Phone Number" required className="!text-[2.1rem]">
              <FormInput
                placeholder="+1 555 123 4567"
                className="sm:!text-[2.1rem]"
                type="tel"
                error={!!errors.phone}
                {...register('phone')}
                autoComplete="tel"
              />
              {errors.phone && (
                <span className="mt-2 text-[1.6rem] text-red-500">
                  {errors.phone.message}
                </span>
              )}
            </FormLabel>

            <FormLabel
              value="Where are you currently selling? Do you use an inventory / order system?"
              required
              className="!text-[2.1rem]"
            >
              <FormTextarea
                placeholder="Shopify, eBay, ShipStation, Amazon, ChannelAdvisor, etc."
                className="sm:!text-[2.1rem]"
                error={!!errors.currentSelling}
                {...register('currentSelling')}
              />
              {errors.currentSelling && (
                <span className="mt-2 text-[1.6rem] text-red-500">
                  {errors.currentSelling.message}
                </span>
              )}
            </FormLabel>

            <button
              type="submit"
              disabled={loading || submitted}
              className={`flex w-full items-center justify-center gap-x-2 rounded-[2rem] py-6 text-[2.1rem] font-semibold text-white transition-all duration-150 ease-linear ${
                submitted
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-brand-secondary'
              }`}
            >
              {loading
                ? 'Submitting...'
                : submitted
                ? 'Thank you!'
                : 'Start Selling with MX Locker'}
              {submitted ? (
                <CheckCircleIcon className="h-12 w-12 text-white" />
              ) : (
                <ChevronRightIcon className="h-12 w-12 text-white" />
              )}
            </button>

            <p className="text-[1.8rem] font-medium text-zinc-800">
              For more information on integrating with MX Locker, please reach
              out to us at{' '}
              <a
                href="mailto:info@mxlocker.com"
                className="text-brand-secondary"
              >
                info@mxlocker.com
              </a>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
